import { useCallback, useEffect, useRef, useState } from 'react';

import { MENU_ITEM_CLASS, MENU_NOTICE_CLASS, MENU_SURFACE_CLASS } from '@/_shared/components/ui/menu-surface';
import { cn } from '@/_shared/lib/utils';

export interface MentionMenuItem {
  id: string;
  label: string;
  /** Second line under the label. Rows without one are a single `h-8` line, like a dropdown row. */
  description?: string;
  /** Trailing content on the label row, e.g. a Badge. */
  badge?: React.ReactNode;
}

interface MentionMenuProps {
  items: MentionMenuItem[];
  onSelect: (item: MentionMenuItem) => void;
  onClose: () => void;
  /** Accessible name for the listbox, e.g. "Skills". */
  label: string;
  /** Replaces the list entirely: loading, nothing matched, or a cap reached. */
  notice?: string;
  /** `data-testid` for the surface. Options get `${optionPrefix}-${item.id}`, as does their DOM id. */
  testId: string;
  optionPrefix: string;
  /** DOM id for the listbox, so the composer can name it in `aria-controls`. */
  id: string;
  /**
   * Reports the active option's DOM id on every change. `aria-activedescendant` only does anything
   * on the FOCUSED element, and focus stays in the composer — so the composer has to carry it.
   */
  onActiveOptionChange?: (optionId: string | undefined) => void;
}

/**
 * The autocomplete surface the composer opens from a trigger character — "/" for skills, "@" for
 * dashboard panels.
 *
 * It is a listbox rather than a `DropdownMenu` because the caret stays in the textarea the whole
 * time: Radix's menu moves real DOM focus onto each item and spends letter keys on its own typeahead,
 * which would stop the user typing mid-sentence. The look comes from the same class constants the
 * dropdown uses (`menu-surface.ts`), so this reads as the same menu without being one.
 */
export function MentionMenu({
  items,
  onSelect,
  onClose,
  label,
  notice,
  testId,
  optionPrefix,
  id,
  onActiveOptionChange,
}: MentionMenuProps): React.ReactElement {
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);

  // Render-phase reset (React's documented alternative to setState-in-effect), keyed on the item
  // identity — a narrowing query must never leave activeIndex past the end of the list.
  const itemIds = items.map((item) => item.id).join(',');
  const [prevItemIds, setPrevItemIds] = useState(itemIds);
  if (itemIds !== prevItemIds) {
    setPrevItemIds(itemIds);
    setActiveIndex(0);
  }

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      // A notice replaces the list, so there is nothing to navigate even when `items` is non-empty.
      // Escape still closes — that is the one key that means the same thing in either state.
      if (notice !== undefined || items.length === 0) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, items.length - 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        const item = items[activeIndex];
        if (item) onSelect(item);
      }
    },
    [activeIndex, items, notice, onSelect, onClose],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    const item = listRef.current?.children[activeIndex] as HTMLElement | undefined;
    item?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const activeOptionId =
    notice === undefined && items[activeIndex] ? `${optionPrefix}-${items[activeIndex].id}` : undefined;

  useEffect(() => {
    onActiveOptionChange?.(activeOptionId);
    return () => onActiveOptionChange?.(undefined);
  }, [activeOptionId, onActiveOptionChange]);

  const surfaceClass = cn(MENU_SURFACE_CLASS, 'absolute bottom-full left-0 mb-1 w-72');

  if (notice !== undefined) {
    return (
      <div id={id} role="listbox" aria-label={label} className={surfaceClass} data-testid={testId}>
        <div className={MENU_NOTICE_CLASS}>{notice}</div>
      </div>
    );
  }

  return (
    <div id={id} role="listbox" aria-label={label} className={surfaceClass} data-testid={testId}>
      <ul ref={listRef} className="max-h-64 overflow-y-auto">
        {items.map((item, idx) => (
          <li key={item.id} id={`${optionPrefix}-${item.id}`} role="option" aria-selected={idx === activeIndex}>
            <button
              type="button"
              onClick={() => onSelect(item)}
              // Pointer and keyboard drive the same highlight, so there is only ever one active row.
              onMouseEnter={() => setActiveIndex(idx)}
              data-highlighted={idx === activeIndex || undefined}
              className={cn(
                MENU_ITEM_CLASS,
                'w-full text-left',
                item.description ? 'flex-col items-start gap-0.5 py-1.5' : 'h-8',
              )}
              data-testid={`${optionPrefix}-${item.id}`}
            >
              <span className="flex w-full min-w-0 items-center gap-1.5">
                <span className="truncate font-medium">{item.label}</span>
                {item.badge}
              </span>
              {item.description !== undefined && (
                <span className="text-xs text-muted-foreground">{item.description}</span>
              )}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
